/**
 * DashboardApi — typed client interface for the Overview screen.
 *
 * Implementations:
 *   - api.mock.ts  — MSW `http` handlers + `createMockDashboardApi()`
 *   - (future)     — openapi-fetch generated client against `sentinel-api`
 *
 * These shapes are provisional; they will be replaced by OpenAPI-generated
 * types once `sentinel-api` ships (ADR-0013 §"Translation strategy").
 */

export type ReplayStatus = 'RUNNING' | 'AWAITING SIG' | 'SEALED' | 'FAILED' | 'MONITOR';

export interface RecentRun {
  receipt: string;
  scenario: string;
  window: string;
  parity: string;
  status: ReplayStatus;
  /** Duration in ms (optional; 0 means "in-flight"). */
  durationMs: number;
}

export interface DriftFlag {
  id: string;
  ruleId: string;
  severity: 'info' | 'warn' | 'fail';
  detectedAt: string;
  summary: string;
}

export interface EvidenceApproval {
  id: string;
  receipt: string;
  subject: string;
  state: 'AWAITING SIG' | 'SEALED';
  quorum: string;
}

export interface SourceSyncHealth {
  source: string;
  eventsPerSecond: number;
  lagSeconds: number;
  state: 'OK' | 'LAGGING' | 'STALLED';
}

export interface ValidationIssue {
  id: string;
  kind: string;
  count: number;
  lastSeen: string;
}

/**
 * Time window for the dashboard's "recent runs" query. Driven by the
 * time-window selector — `24h` is the default, matching BRD §13.
 */
export type DashboardTimeWindow = '24h' | '7d' | '30d';

export interface RecentRunsQuery {
  window: DashboardTimeWindow;
}

/**
 * Overview KPI strip — parity, replay count, open divergences, sealed
 * packages, and event volume. Backed by `/api/v1/dashboard/kpis` so the
 * numbers never lag the underlying replay outcome.
 */
export interface DashboardKpis {
  parity30d: string;
  replays30d: number;
  divergencesOpen: number;
  sealedPackages: number;
  events24h: string;
}

export interface DashboardApi {
  getRecentRuns(query?: RecentRunsQuery): Promise<RecentRun[]>;
  getDriftFlags(): Promise<DriftFlag[]>;
  getPendingEvidenceApprovals(): Promise<EvidenceApproval[]>;
  getSourceSyncHealth(): Promise<SourceSyncHealth[]>;
  getTopValidationIssues(): Promise<ValidationIssue[]>;
  getKpis(): Promise<DashboardKpis>;
}

/** Endpoint paths used by the MSW handlers and the eventual real client. */
export const DASHBOARD_ENDPOINTS = {
  recentRuns: '/api/v1/dashboard/recent-runs',
  driftFlags: '/api/v1/dashboard/drift-flags',
  evidenceApprovals: '/api/v1/dashboard/evidence-approvals',
  sourceSyncHealth: '/api/v1/dashboard/source-sync-health',
  topValidationIssues: '/api/v1/dashboard/top-validation-issues',
  kpis: '/api/v1/dashboard/kpis',
} as const;
